import React, { useState, useEffect } from "react";
import RightSidebar from "../../../sidebar/RightSideBar";
import { ChevronDown, Info, Calendar, Clock } from "lucide-react";
import { getMemberAvailability, addBooking, getMemberBookings, getMeetingSettings } from "../../../../services/meeting";
import { toast } from "react-toastify";
import moment from "moment";
import { computeEventUtcInstant } from "../../../../utils/timezoneFormatter";
import { useTimezoneStore } from "../../../../store/useTimezoneStore";

const BookAppointmentSidebar = ({ 
  isOpen, 
  onClose, 
  member, 
  exhibitorId, 
  eventData, 
  siteUser,
  onBookingSuccess 
}) => {
  const [loading, setLoading] = useState(false);
  const [availabilityData, setAvailabilityData] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [memberSettings, setMemberSettings] = useState(null);

  useEffect(() => {
    if (isOpen && exhibitorId) {
      fetchAvailability();
    }
  }, [isOpen, exhibitorId]);

  const fetchAvailability = async () => {
    try {
      setLoading(true);
      const [availResponse, bookingResponse, settingsResponse] = await Promise.all([
        getMemberAvailability(member?.id),
        getMemberBookings(member?.id),
        getMeetingSettings(member?.id)
      ]);

      if (availResponse.data.success) {
        const memberAvailability = (availResponse.data.data?.data || availResponse.data.data || [])
          .filter(a => a.booth_member_id === member?.id && a.is_active !== 0);
        setAvailabilityData(memberAvailability);
        
        if (memberAvailability.length > 0) {
          const firstDay = getEventDays().find(d => 
            memberAvailability.some(a => moment(a.availability_date).format("YYYY-MM-DD") === d.rawDate.format("YYYY-MM-DD"))
          );
          if (firstDay) setSelectedDate(firstDay);
        }
      }

      if (bookingResponse.data.success) {
        const memberBookings = bookingResponse.data.data?.data || bookingResponse.data.data || [];
        // Filter out cancelled bookings
        setBookedSlots(memberBookings.filter(b => b.status !== 'cancelled'));
      }

      if (settingsResponse.data.success) {
        // Handle nesting as seen in useExhSpaceStore update
        const settings = settingsResponse.data.data?.data || settingsResponse.data.data;
        setMemberSettings(settings);
      }
    } catch (error) {
      console.error("Error fetching availability/bookings/settings:", error);
      toast.error("Failed to load availability slots");
    } finally {
      setLoading(false);
    }
  };

  const getEventDays = () => {
    if (!eventData?.start_date || !eventData?.end_date) return [];
    const start = moment(eventData.start_date);
    const end = moment(eventData.end_date);
    const days = [];
    let current = start.clone();
    let dayNum = 1;
    while (current <= end) {
      days.push({
        dayNum,
        date: current.format("MMM DD, YYYY"),
        dayName: current.format("ddd").toLowerCase(),
        rawDate: current.clone()
      });
      current.add(1, "days");
      dayNum++;
    }
    return days;
  };

  const eventDays = getEventDays();
  const availableDates = new Set(availabilityData.map(a => moment(a.availability_date).format("YYYY-MM-DD")));
  const filteredEventDays = eventDays.filter(day => availableDates.has(day.rawDate.format("YYYY-MM-DD")));

  // Generate slots for the selected date
  const generateSlots = () => {
    if (!selectedDate || !availabilityData.length) return [];
    
    const dateStr = selectedDate.rawDate.format("YYYY-MM-DD");
    const dayRules = availabilityData.filter(a => moment(a.availability_date).format("YYYY-MM-DD") === dateStr);
    const slots = [];

    const eventTz = eventData?.timezone || useTimezoneStore.getState().getActiveTimezone();

    dayRules.forEach(rule => {
      const startTime = moment(rule.start_time, "HH:mm:ss");
      const endTime = moment(rule.end_time, "HH:mm:ss");
      // Prioritize setting duration over rule duration
      const duration = memberSettings?.meeting_duration || rule.slot_duration || 15;

      let current = startTime.clone();
      while (current.clone().add(duration, "minutes") <= endTime) {
        const slotEnd = current.clone().add(duration, "minutes");
        const startInstant = computeEventUtcInstant(dateStr, `${current.format("HH:mm")}:00`, eventTz);
        const endInstant = computeEventUtcInstant(dateStr, `${slotEnd.format("HH:mm")}:00`, eventTz);

        const fullStart = startInstant ? startInstant.toISOString() : selectedDate.rawDate.clone().set({
          hour: current.hour(),
          minute: current.minute(),
          second: 0
        }).toISOString();
        
        const fullEnd = endInstant ? endInstant.toISOString() : selectedDate.rawDate.clone().set({
          hour: slotEnd.hour(),
          minute: slotEnd.minute(),
          second: 0
        }).toISOString();

        // Check if this slot is already booked and by whom
        const overlappingBooking = bookedSlots.find(booking => {
          const bStart = moment(booking.start_datetime);
          const bEnd = moment(booking.end_datetime);
          const currentFullStart = moment(fullStart);
          const currentFullEnd = moment(fullEnd);
          return moment.max(currentFullStart, bStart) < moment.min(currentFullEnd, bEnd);
        });

        let status = 'available';
        if (overlappingBooking) {
          status = (overlappingBooking.attendee_user_id === siteUser?.id) ? 'mine' : 'booked';
        }

        slots.push({
          start: current.format("hh:mm A"),
          end: slotEnd.format("hh:mm A"),
          startTimeLabel: current.format("HH:mm:ss"),
          endTimeLabel: slotEnd.format("HH:mm:ss"),
          fullStart,
          fullEnd,
          status
        });

        current.add(duration, "minutes");
      }
    });

    return slots;
  };

  const slots = generateSlots();

  const handleBook = async () => {
    if (!selectedSlot) {
      toast.error("Please select a time slot");
      return;
    }

    try {
      setBookingLoading(true);
      const payload = {
        exhibitor_id: exhibitorId,
        booth_member_id: member.id,
        attendee_name: `${siteUser.name} ${siteUser.last_name || ""}`.trim(),
        attendee_email: siteUser.email,
        attendee_user_id: siteUser.id,
        start_datetime: selectedSlot.fullStart,
        end_datetime: selectedSlot.fullEnd,
        notes: "",
        consent_given: 1
      };

      const response = await addBooking(payload);
      console.log("Booking Response:", response.data); // Debug log

      if (response.data.success) {
        toast.success("Meeting booked successfully!");
        
        // Robust ID extraction
        const bookingId = response.data.data?.id || response.data?.id || response.data?.data?.bookingId;

        onBookingSuccess({
          id: bookingId,
          exhibitor_name: eventData.exhibitor_name || "Exhibitor", // Fallback
          event_name: eventData.event_name || "Quick Events",
          date: selectedDate.date,
          time: `${selectedSlot.start} - ${selectedSlot.end}`,
          exhibitor_id: exhibitorId,
          start_datetime: selectedSlot.fullStart,
          end_datetime: selectedSlot.fullEnd,
          isAutoApproved: !!memberSettings?.auto_accept
        });
        onClose();
      }
    } catch (error) {
      console.error("Error booking meeting:", error);
      toast.error(error.response?.data?.errorMessage || "Failed to book meeting. The slot might be taken.");
    } finally {
      setBookingLoading(false);
    }
  };

  return (
    <RightSidebar
      isOpen={isOpen}
      onClose={onClose}
      title="Book Appointment"
      widthClass="sm:w-[450px]"
      contentPadding="p-0"
    >
      <div className="flex flex-col h-full bg-white">
        <div className="flex-1 overflow-y-auto px-6 py-8 space-y-8">
          {/* Date Selector */}
          <div className="space-y-3">
            <label className="text-base font-bold text-gray-900 leading-none">
              Date
            </label>
            <div className="relative group">
              <div className="flex items-center justify-between gap-3 px-4 py-3 border border-slate-200 rounded-xl bg-white shadow-sm hover:border-[#1E374D] transition-all relative cursor-pointer">
                <span className="text-sm font-bold text-gray-700">
                  {selectedDate
                    ? `Day - ${selectedDate.dayNum} : ${selectedDate.rawDate.format("ddd, MMM DD, YYYY")}`
                    : "Select Date"}
                </span>
                <ChevronDown size={18} className="text-gray-400" />
                <select
                  className="absolute inset-0 opacity-0 cursor-pointer w-full"
                  onChange={(e) => {
                    const day = eventDays.find(
                      (d) => d.dayNum === parseInt(e.target.value),
                    );
                    setSelectedDate(day);
                    setSelectedSlot(null);
                  }}
                  value={selectedDate?.dayNum || ""}
                >
                  <option value="" disabled>
                    Select Date
                  </option>
                  {filteredEventDays.map((day) => (
                    <option key={day.dayNum} value={day.dayNum}>
                      Day - {day.dayNum} :{" "}
                      {day.rawDate.format("ddd, MMM DD, YYYY")}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Slots Selector */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold text-gray-900 leading-none">
                Pick an option below
              </h3>
              {memberSettings && (
                <div className="flex items-center gap-1.5 px-3 py-1 bg-indigo-50 text-indigo-700 rounded-full border border-indigo-100">
                  <Clock size={12} className="stroke-[3]" />
                  <span className="text-[11px] font-black uppercase tracking-tight">
                    {memberSettings.meeting_duration} Min Meeting
                  </span>
                </div>
              )}
            </div>

            {loading ? (
              <div className="py-10 flex flex-col items-center justify-center gap-2">
                <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
                <p className="text-xs text-gray-500 font-bold">
                  Loading slots...
                </p>
              </div>
            ) : slots.length > 0 ? (
              <div className="grid grid-cols-2 gap-4">
                {slots.map((slot, idx) => {
                  const isSelected = selectedSlot?.fullStart === slot.fullStart;
                  const isBooked = slot.status === "booked";
                  const isMine = slot.status === "mine";

                  return (
                    <button
                      key={idx}
                      disabled={isBooked || isMine}
                      onClick={() => setSelectedSlot(slot)}
                      className={`relative px-4 py-4 rounded-xl text-sm font-bold transition-all border flex flex-col items-center justify-center gap-1 ${
                        isSelected
                          ? "bg-[#4CAF50] text-white border-[#4CAF50] shadow-md ring-2 ring-[#4CAF50]/30"
                          : isMine
                            ? "bg-sky-50 text-sky-700 border-sky-200 cursor-not-allowed"
                            : isBooked
                              ? "bg-slate-50 text-slate-400 border-slate-100 cursor-not-allowed"
                              : "bg-white text-gray-800 border-slate-200 hover:border-indigo-500 hover:text-indigo-600 shadow-sm"
                      }`}
                    >
                      <span>
                        {slot.start} - {slot.end}
                      </span>
                      {isMine && (
                        <span className="text-[10px] font-black uppercase tracking-tighter opacity-80">
                          Your Meeting
                        </span>
                      )}
                      {isBooked && (
                        <span className="text-[10px] font-black uppercase tracking-tighter opacity-60">
                          Occupied
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 text-center">
                <Calendar className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500 font-medium">
                  No availability slots for this date.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar Footer */}
        <div className="px-6 py-6 border-t border-slate-100 flex items-center justify-between gap-3 bg-[#F8FAFC]">
          <button
            onClick={onClose}
            className="px-6 py-2.5 rounded-lg border border-gray-300 hover:bg-gray-100 text-gray-800 font-bold text-sm transition-colors"
          >
            CANCEL
          </button>
          <button
            onClick={handleBook}
            disabled={!selectedSlot || bookingLoading}
            className={`site-color-btn  ${
              !selectedSlot || bookingLoading
              ? "bg-indigo-400 text-white cursor-not-allowed opacity-40"
              : ""
            }`}
          >
            {bookingLoading ? "BOOKING..." : "BOOK"}
          </button>
        </div>
      </div>
    </RightSidebar>
  );
};

export default BookAppointmentSidebar;
